import { FormSelect, Popover, PrimaryButton, Tooltip } from '@systemeio/ui'
import { ActionListContent } from 'modules/automation-rules/actions/components/action-list-content'
import { TriggerBadge } from 'modules/automation-rules/actions/components/trigger-badge'
import { actionTitleLocoKeys } from 'modules/automation-rules/actions/constants/action-loco-keys'
import { useFilterableActionData } from 'modules/automation-rules/actions/hooks/use-filterable-action-data'
import {
  AUTOMATION_RULES_API,
  AUTOMATION_RULES_FILTER_API,
  onChangeRuleStatus,
  onDeleteRule,
} from 'modules/automation-rules/api/automation-rules-api'
import { automationRuleDefaultFilters } from 'modules/automation-rules/constants/automation-rule-default-values'
import { AutomationRuleLoco } from 'modules/automation-rules/constants/automation-rule-loco'
import { useAutomationRules } from 'modules/automation-rules/hooks/use-automation-rules'
import { TriggerListContent } from 'modules/automation-rules/triggers/components/trigger-list-content'
import { triggerActionLocoKeys } from 'modules/automation-rules/triggers/constants/trigger-loco-keys'
import { AutomationRuleTriggersEnum } from 'modules/automation-rules/triggers/enums/trigger-enum'
import { useFilterableTriggerData } from 'modules/automation-rules/triggers/hooks/use-filterable-trigger-data'
import {
  AutomationRuleFilterInterface,
  RuleStatusFilterInterface,
} from 'modules/automation-rules/types/automation-rule-filter-interface'
import { AutomationRuleInterface } from 'modules/automation-rules/types/automation-rule-interface'
import { NextPage } from 'next'
import { useRouter } from 'next/router'
import React, { useState } from 'react'
import ConfirmationModal from 'shared/components/confirmation-modal'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import ListingLayout from 'shared/components/listing-layout'
import { Table } from 'shared/components/table/table'
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface'
import { BooleanStatusEnum } from 'shared/enums/status-enum'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { useUpdateData } from 'shared/hooks/use-update-data'
import useUser from 'shared/hooks/use-user'
import { useFilterPagination } from 'shared/route-query/hooks/use-filter-pagination'
import { dateTimeFormat } from 'shared/utils/date-time-format'
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props'
import { AddIcon, CheckCircleIcon, QuestionMarkIcon } from '@systemeio/ui/icons'

const AutomationRule: NextPage = () => {
  const { filter, setFilter, setPagination, pagination, setLimit, limit } = useFilterPagination(
    automationRuleDefaultFilters,
  )

  const updateData = useUpdateData({
    api: AUTOMATION_RULES_API,
    constantApi: [AUTOMATION_RULES_FILTER_API],
  })

  const [responseFilter, setResponseFilter] = useState<AutomationRuleFilterInterface>(
    automationRuleDefaultFilters,
  )

  const { automationRules, counter } = useAutomationRules({ limit, pagination, filter })

  const { t } = useLocoTranslation()
  const { user } = useUser()
  const router = useRouter()

  const filterableTrigger = useFilterableTriggerData(responseFilter)
  const filterableAction = useFilterableActionData(responseFilter)

  const statusData: RuleStatusFilterInterface[] = Object.keys(BooleanStatusEnum).map(el => {
    const key = el as keyof typeof BooleanStatusEnum
    return {
      caption: t(BooleanStatusEnum[key]),
      id: key,
    }
  })

  const columns: ColumnDefinitionInterface<AutomationRuleInterface>[] = [
    {
      key: 'triggers',
      header: t('dashboard.automation_rule.trigger.title'),
      widthPercent: 30,
      onRender: automationRule => (
        <div className={'flex w-full flex-col'}>
          {automationRule.triggers.map((el, idx) => (
            <div
              key={`${el.type}-${idx}`}
              className={`flex flex-col gap-1 py-3 ${
                idx !== 0 && 'border-text-secondary/30 border-t'
              } ${idx === 0 && 'pt-0'} ${idx + 1 === automationRule.triggers?.length && 'pb-0'}`}
            >
              <div className={'text-text-primary flex flex-row items-center gap-2'}>
                <span>{t(triggerActionLocoKeys[el.type])}</span>
                {el.type === AutomationRuleTriggersEnum.form_subscribed && (
                  <TriggerBadge target={el} />
                )}
              </div>
              <span className={'text-xs'}>
                <TriggerListContent trigger={el} />
              </span>
            </div>
          ))}
        </div>
      ),
    },
    {
      key: 'actions',
      header: t('dashboard.automation_rule.action.title'),
      widthPercent: 30,
      onRender: automationRule => (
        <div className={'flex w-full flex-col'}>
          {automationRule.actions.map((el, idx) => (
            <div
              key={`${el.type}-${idx}`}
              className={`flex flex-col gap-1 py-3 ${
                idx !== 0 && 'border-text-secondary/30 border-t'
              } ${idx === 0 && 'pt-0'} ${idx + 1 === automationRule.actions?.length && 'pb-0'}`}
            >
              <span className={'text-text-primary'}>{t(actionTitleLocoKeys[el.type])}</span>
              <span className={'text-xs'}>
                <ActionListContent action={el} />
              </span>
            </div>
          ))}
        </div>
      ),
    },
    {
      key: 'active',
      header: t('global.status'),
      widthPercent: 10,
      onRender: automationRule => (
        <div className="flex items-center lg:ml-4">
          <Tooltip
            label={automationRule.active ? t('global.active') : t('global.inactive')}
            mode="hover"
            key="remove-tooltip"
          >
            <CheckCircleIcon
              className={`h-[16px] w-[16px] sm:h-[20px] sm:w-[20px] ${
                automationRule.active && 'fill-green'
              }`}
            />
          </Tooltip>
        </div>
      ),
    },
    {
      key: 'createdAt',
      header: t('global.created_on'),
      widthPercent: 20,
      onRender: automationRule => (
        <span>
          {dateTimeFormat(automationRule.createdAt, {
            timeFormat: user?.timeFormat,
            dateFormat: user?.dateFormat,
            timeZone: user?.timezone,
          })}
        </span>
      ),
    },
  ]

  const deleteRule = async (rule: AutomationRuleInterface) => {
    await onDeleteRule(rule.id)
    await setFilter(automationRuleDefaultFilters)
    await updateData(false, automationRules)
  }

  const enableRule = async (rule: AutomationRuleInterface) => {
    await onChangeRuleStatus(false, rule.id)
    setFilter(automationRuleDefaultFilters)
    await updateData(false, automationRules)
  }
  const disableRule = async (rule: AutomationRuleInterface) => {
    await onChangeRuleStatus(true, rule.id)
    setFilter(automationRuleDefaultFilters)
    await updateData(false, automationRules)
  }

  return (
    <ListingLayout
      counter={counter}
      data={automationRules}
      title={
        <div className={'flex items-center gap-1.5'}>
          {t('dashboard.automation_rule.title_plural')}
          <Popover
            label={t('dashboard.automation_rule.title_tooltip')}
            popoverClassName={'w-[200px] sm:w-[400px]'}
          >
            {show => (
              <QuestionMarkIcon
                className={`${
                  show ? 'fill-brand-primary' : 'fill-fill-primary'
                } group-focus-visible:fill-brand-primary`}
              />
            )}
          </Popover>
        </div>
      }
      customHeaderElement={
        <PrimaryButton
          as={LinkWithoutPrefetch}
          href={'/automation-rules/create'}
          aria-disabled={!user}
        >
          <AddIcon />
          <span className="hidden sm:flex"> {t('global.create')}</span>
        </PrimaryButton>
      }
      type={'side'}
      filterData={{
        filterer: {
          filter,
          setFilter,
          defaultFilters: automationRuleDefaultFilters,
          setResponseFilter,
        },
        paginator: { pagination, setPagination },
        limiter: { limit, setLimit },
        filterable: [
          ...filterableTrigger,
          ...filterableAction,
          {
            field: 'status',
            onRender: (value, onChange) => (
              <FormSelect
                data={statusData}
                key={'rule-status-filter'}
                placeholder={t('dashboard.automation_rule.choose_status')}
                value={value}
                onChange={data => onChange(data)}
              />
            ),
          },
        ],
      }}
    >
      <Table
        onRowClick={rule => router.push(`/automation-rules/${rule.id}`)}
        caption={t('dashboard.automation_rule.title')}
        actions={{
          openLink: [
            {
              name: 'edit-rule',
              isNext: true,
              openInNew: false,
              caption: t('dashboard.actions.title.edit'),
              setHref: automationRule => `/automation-rules/${automationRule.id}`,
            },
          ],
          custom: [
            {
              name: 'enable-rule',
              caption: t('dashboard.automation_rule.edit.enable'),
              shouldRender: rule => !rule.active,
              onActionRender: (opened, onClose, instance, key) => (
                <ConfirmationModal
                  key={key}
                  confirmationText={t('dashboard.automation_rule.edit.enable_confirm')}
                  toastCaption={t('dashboard.automation_rule.edit.enable_success')}
                  opened={opened}
                  onClose={onClose}
                  onConfirm={() => enableRule(instance)}
                />
              ),
            },
            {
              name: 'disable-rule',
              caption: t('dashboard.automation_rule.edit.disable'),
              shouldRender: rule => rule.active,
              onActionRender: (opened, onClose, instance, key) => (
                <ConfirmationModal
                  key={key}
                  confirmationText={t('dashboard.automation_rule.edit.disable_confirm')}
                  toastCaption={t('dashboard.automation_rule.edit.disable_success')}
                  opened={opened}
                  onClose={onClose}
                  onConfirm={() => disableRule(instance)}
                />
              ),
            },
            {
              name: 'delete-rule',
              caption: t('global.delete'),
              onActionRender: (opened, onClose, instance, key) => (
                <ConfirmationModal
                  key={key}
                  toastCaption={t('dashboard.automation_rule.edit.delete_success')}
                  confirmationText={t('dashboard.automation_rule.edit.delete_confirm')}
                  opened={opened}
                  onClose={onClose}
                  onConfirm={() => deleteRule(instance)}
                />
              ),
            },
          ],
        }}
        data={automationRules}
        columns={columns}
        limiter={{ limit, setLimit }}
        paginator={{ pagination, setPagination }}
      />
    </ListingLayout>
  )
}

export const getServerSideProps = getBaseServerSideProps(
  AutomationRuleLoco,
  'header.top_menu.automations',
)

export default AutomationRule
